import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore, getEffectiveTheme } from '../../stores/uiStore';
import { authApi } from '../../services/api';
import { toast } from '../../stores/toastStore';
import { MenuIcon, MoonIcon, SunIcon, SystemIcon } from '../icons';
import './Header.css';

/**
 * Header component with user menu and mobile sidebar toggle.
 * Provides logout functionality and theme switching.
 * 
 * Requirements: 10.1, 10.2
 */
export function Header() {
  const navigate = useNavigate();
  const { admin, logout } = useAuthStore();
  const { theme, setTheme, toggleSidebar } = useUIStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
      logout();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch {
      // Even if API fails, clear local state
      logout();
      navigate('/login');
    }
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    if (nextTheme) {
      setTheme(nextTheme);
    }
  };

  const getThemeIcon = () => {
    const effectiveTheme = getEffectiveTheme(theme);
    if (theme === 'system') return <SystemIcon size={20} />;
    return effectiveTheme === 'dark' ? <MoonIcon size={20} /> : <SunIcon size={20} />;
  };

  return (
    <header className="header">
      <div className="header-left">
        <button
          className="header-menu-btn"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <MenuIcon size={24} />
        </button>
      </div>

      <div className="header-right">
        <button
          className="header-theme-btn"
          onClick={cycleTheme}
          aria-label={`Current theme: ${theme}. Click to change.`}
          title={`Theme: ${theme}`}
        >
          {getThemeIcon()}
        </button>

        <div className="header-user-menu" ref={menuRef}>
          <button
            className="header-user-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            <span className="header-user-avatar">
              {admin?.email?.charAt(0).toUpperCase() || '?'}
            </span>
            <span className="header-user-info">
              <span className="header-user-email">{admin?.email || 'Unknown'}</span>
              <span className="header-user-role">
                {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
              </span>
            </span>
          </button>
          
          {menuOpen && (
             <div className="header-dropdown">
               <button onClick={handleLogout} className="header-dropdown-item">
                 Log out
               </button>
             </div>
          )}
        </div>
      </div>
    </header>
  );
}
